import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { withNext } from "../utils/authRedirect";
import { useSetHelpMessage } from "../context/HelpContext";
import { useSettings } from "../context/SettingsContext";
import Spinner from "../components/Spinner";
import RichTextRenderer from "../components/RichTextRenderer";
import SEOHead from "../components/SEOHead";
import { CheckCircle2, Package, Lock, BookOpen, Award, Users, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { getPublicPath, getSeoPage } from "../utils/seoPages";

export default function PackDetail() {
  const { packId } = useParams();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref") || localStorage.getItem("sf_ref") || "";
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();
  const packsPath = getPublicPath(settings, "/packs");
  const packsSeo = getSeoPage(settings, "/packs");

  useEffect(() => {
    api.get(`/packs/${packId}`).then((r) => setPack(r.data)).finally(() => setLoading(false));
  }, [packId]);

  useSetHelpMessage(
    pack ? `Bonjour ! J'ai une question sur le pack "${pack.title}" (${pack.price_eur}€).` : null,
    [pack?.title, pack?.price_eur]
  );

  const handleBuy = () => {
    const checkoutPath = `/checkout/pack/${pack.pack_id}${ref ? `?ref=${ref}` : ""}`;
    // Sans compte : inscription d'abord, puis reprise automatique du paiement.
    if (!user) { navigate(withNext("/register", checkoutPath)); return; }
    navigate(checkoutPath);
  };

  if (loading) return <Spinner className="min-h-[60vh]" />;
  if (!pack) return <p className="text-center mt-16">Pack introuvable.</p>;

  const savings = (pack.original_total || 0) - pack.price_eur;
  const savingsPct = pack.original_total > 0 ? Math.round((savings / pack.original_total) * 100) : 0;
  const cleanShortDescription = (pack.short_description || "").replace(/<[^>]+>/g, "").trim();
  const seoTitle = pack.seo_title || `${pack.title} — SF Learning`;
  const seoDescription = pack.seo_description || cleanShortDescription || packsSeo.description || "Pack de formations SF Learning";
  const packImageAlt = pack.image_alt || pack.seo_subtitle || pack.seo_title || pack.title || "Pack de formations SF Learning";

  return (
    <div data-testid="pack-detail-page">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        image={pack.cover_image}
        imageAlt={packImageAlt}
        url={typeof window !== "undefined" ? window.location.href : ""}
        type="product"
        modifiedTime={pack.updated_at}
        author="SF Learning"
      />
      <section className="bg-white border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="badge"><Package className="w-3.5 h-3.5" /> Pack</span>
              {pack.badge && <span className="badge orange"><Sparkles className="w-3.5 h-3.5" /> {pack.badge}</span>}
              {savings > 0 && <span className="badge orange">Économise {savings.toFixed(0)}€ ({savingsPct}%)</span>}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight">{pack.title}</h1>
            <RichTextRenderer html={pack.short_description} className="text-lg text-gray-700 mt-4" />

            <div className="mt-6 flex items-center gap-6 text-sm text-gray-600 flex-wrap">
              <span className="inline-flex items-center gap-1"><BookOpen className="w-4 h-4 text-brand-green" /> {(pack.courses || []).length} formations</span>
              <span className="inline-flex items-center gap-1"><Award className="w-4 h-4 text-brand-green" /> Devoirs corrigés</span>
              <span className="inline-flex items-center gap-1"><Users className="w-4 h-4 text-brand-green" /> Communauté incluse</span>
            </div>

            <div className="mt-8 prose-sf max-w-none">
              <h2>À propos de ce pack</h2>
              <RichTextRenderer html={pack.long_description} />
            </div>

            <div className="mt-10">
              <h2 className="font-heading text-2xl font-bold mb-5 text-brand-green">Ce que tu reçois ({(pack.courses || []).length} formations)</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(pack.courses || []).map((c, i) => (
                  <motion.div
                    key={c.course_id}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    viewport={{ once: true }}
                    className="card p-4 flex gap-3"
                    data-testid={`pack-course-${c.course_id}`}
                  >
                    {c.cover_image && <img src={c.cover_image} alt={c.title || "Formation incluse dans le pack"} className="w-20 h-20 object-cover rounded-lg flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-bold line-clamp-2">{c.title}</p>
                      <RichTextRenderer html={c.short_description} className="text-xs text-gray-500 line-clamp-2 mt-1" />
                      <p className="text-sm text-brand-green font-bold mt-2">{c.price_eur}€ <span className="text-xs text-gray-400 line-through ml-1">au prix unitaire</span></p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-[100px] self-start">
            <div className="card overflow-hidden">
              {pack.cover_image && <img src={pack.cover_image} alt={packImageAlt} className="w-full aspect-video object-cover" />}
              <div className="p-6">
                {savings > 0 && (
                  <p className="text-sm text-gray-400 line-through">{pack.original_total.toFixed(2)}€ valeur totale</p>
                )}
                <p className="font-heading text-4xl font-black text-brand-green">{pack.price_eur}€</p>
                {savings > 0 && (
                  <p className="text-sm text-brand-orange font-semibold mt-1">Tu économises {savings.toFixed(2)}€ ({savingsPct}%)</p>
                )}
                <button onClick={handleBuy} className="btn-primary w-full justify-center mt-4" data-testid="buy-pack-btn">
                  <Package className="w-4 h-4" /> Acheter le pack
                </button>
                <ul className="mt-5 space-y-2 text-sm">
                  {[`${(pack.courses || []).length} formations complètes`, "Accès à vie aux contenus", "Devoirs corrigés sous 48h", "Communauté incluse", `Économise ${savingsPct}% sur prix unitaire`].map((t, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-green mt-0.5" /> {t}</li>
                  ))}
                </ul>
                {ref && <p className="text-xs text-amber-700 mt-3 bg-amber-50 p-2 rounded">Recommandé par <strong>{ref}</strong></p>}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
